/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { PriceLine, ProductPrice, SalesAgreementPriceLine } from '@msdyn365-commerce/retail-proxy';
import { ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { format } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

const defaultPrice: number = 0;
const freePrice: number = 0;

/**
 * Interface for price component resources.
 * @param {string} priceRangeSeparator - The price range separator.
 */
export interface IPriceComponentResources {
    priceRangeSeparator?: string;
    salesAgreementPricePrompt?: string;
    salesAgreementExpirationDatePrompt?: string;
    salesAgreementCommittedQuantityPrompt?: string;
    salesAgreementRemainingQuantityPrompt?: string;
}

/**
 * Interface for price component props.
 * @param {boolean} isPriceMinMaxEnabled - Whether the price range feature is enabled or not.
 * @param {IPriceComponentResources} priceResources - The price range resources.
 */
export interface IPriceComponentProps extends IComponentProps<{ price: ProductPrice }> {
    className?: string;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    isPriceMinMaxEnabled?: boolean;
    isSalesAgreementPriceFeatureEnabled?: boolean;
    salesAgreementLineId?: number;
    priceResources?: IPriceComponentResources;
}

/**
 * The interface of price component.
 */
export interface IPriceComponent extends IComponent<IPriceComponentProps> {
}

const priceComponentActions = {};

/**
 * Get the original price from price component props.
 * @param props - The IPriceComponentProps.
 * @returns The original price.
 */
const getOriginalPrice = (props: IPriceComponentProps): number => {
    return Math.max(
        props.data.price.BasePrice ?? defaultPrice,
        props.data.price.TradeAgreementPrice ?? defaultPrice,
        props.data.price.AdjustedPrice ?? defaultPrice
    );
};

/**
 * Get the formatted currency.
 * @param props - The props.
 * @param price - The price.
 * @param shouldUseFreePriceText - The shouldUseFreePriceText.
 * @returns The formatted currency.
 */
const formatCurrency = (props: IPriceComponentProps, price: number | undefined, shouldUseFreePriceText: boolean | undefined): string => {
    if (price === undefined) {
        return '';
    }

    if (shouldUseFreePriceText && price === freePrice && props.freePriceText) {
        return props.freePriceText;
    }

    return props.context.cultureFormatter.formatCurrency(price);
};

/**
 * ShowStrikethroughPricing.
 * @param props - The props.
 * @returns True if showStrikethroughPricing, false otherwise.
 */
const showStrikethroughPricing = (props: IPriceComponentProps): boolean => {
    const originalPrice = getOriginalPrice(props);
    if (props.isPriceMinMaxEnabled &&
        props.data.price.MaxVariantPrice &&
        props.data.price.MinVariantPrice &&
        props.data.price.MaxVariantPrice > props.data.price.MinVariantPrice) {
        return props.data.price.MinVariantPrice < originalPrice;
    }

    if (originalPrice && props.data.price.CustomerContextualPrice) {
        return originalPrice > props.data.price.CustomerContextualPrice;
    }

    return false;
};

/**
 * Render current price.
 * @param props - The props.
 * @returns The JSX.Element.
 */
const renderCurrentPrice = (props: IPriceComponentProps): JSX.Element => {
    const maxVariantPrice: string = formatCurrency(props, props.data.price.MaxVariantPrice, false);
    const minVariantPrice: string = formatCurrency(props, props.data.price.MinVariantPrice, false);
    if (props.isPriceMinMaxEnabled &&
        props.data.price.MaxVariantPrice &&
        props.data.price.MinVariantPrice &&
        props.data.price.MaxVariantPrice > props.data.price.MinVariantPrice) {
        return (
            <span className='msc-price__pricerange' itemProp='price'>
                <span className='msc-price__minprice' itemProp='price'>
                    {minVariantPrice}
                </span>
                <span className='msc-price__separator' itemProp='price'>
                    {props.priceResources?.priceRangeSeparator}
                </span>
                <span className='msc-price__maxprice' itemProp='price'>
                    {maxVariantPrice}
                </span>
            </span>
        );
    }

    const data = `${formatCurrency(props, props.data.price.CustomerContextualPrice, true)}`
    return (
    <script type='text/javascript' dangerouslySetInnerHTML={{__html: data}}/>
    );
};

/**
 * Render current price with original price.
 * @param props - The props.
 * @returns The JSX.Element.
 */
const renderCurrentPriceWithOriginalPrice = (props: IPriceComponentProps): JSX.Element => {
    const originalPrice: number = getOriginalPrice(props);
    const initialPrice: string = formatCurrency(props, originalPrice, true);

    return (
        <>
            <span className='sr-only'>
                {' '}
                {props.originalPriceText}
                {' '}
                {initialPrice}
                {' '}
                {props.currentPriceText}
                {' '}
                {renderCurrentPrice(props)}
            </span>
            <span className='msc-price__strikethrough' aria-hidden='true'>
                {initialPrice}
                {' '}
            </span>
            <span aria-hidden='true'>
                {renderCurrentPrice(props)}
            </span>
            {props.savingsText && <span className='msc-price__savings'>
                {props.savingsText}
            </span>}
        </>
    );
};

/**
 * Return a JSX.Element contains sales agreement prompt if feature enabled.
 * @param props - The IPriceComponentProps.
 * @returns The JSX.Element.
 */
const renderSalesAgreementPrice = (props: IPriceComponentProps): JSX.Element => {
    const priceLines: PriceLine[] = props.data.price.AttainablePriceLines ?? [];
    const firstPriceLine: SalesAgreementPriceLine = priceLines[0] as SalesAgreementPriceLine;
    return (<script>
        window.dataLayer = window.dataLayer || [];
        <p className='msc-price__actual' itemProp='price'>
            {formatCurrency(props, props.data.price.SalesAgreementPrice ?? props.data.price.CustomerContextualPrice, true)}
        </p>
        <p className={classnames('msc-price__salesAgreementPrompt', props.className)}>
            {props.priceResources?.salesAgreementPricePrompt}
        </p>
        <p className={classnames('msc-price__salesAgreementDetail', props.className)}>
            <span>
                {/* Available date */}
                {format(
                    props.priceResources?.salesAgreementExpirationDatePrompt ?? '',
                    [
                        firstPriceLine.EffectiveDate,
                        firstPriceLine.EffectiveDate
                    ]
                )}
            </span>
            <span>
                {/* Quantity commitment */}
                {format(
                    props.priceResources?.salesAgreementCommittedQuantityPrompt ?? '',
                    [firstPriceLine.CommittedQuantity]
                )}
            </span>
            <span>
                {/* Remaining quantity to qualify */}
                {format(
                    props.priceResources?.salesAgreementRemainingQuantityPrompt ?? '',
                    [firstPriceLine.RemainingQuantity]
                )}
            </span>
        </p>
    </script>);
};

/**
 * Price.
 * @param props - The props.
 * @returns The JSX.Element.
 */
const Price: React.FC<IPriceComponentProps> = (props: IPriceComponentProps): JSX.Element | null => {
    // CustomerContextualPrice could be zero
    if (props.data.price.CustomerContextualPrice === undefined) {
        return null;
    }

    // Determine if contract price is avaiable/applied by:
    //   isFeatureEnabled and SA price != 0, for PDP.
    //   SA line id != 0, for cart line.
    if ((props.isSalesAgreementPriceFeatureEnabled && props.data.price.SalesAgreementPrice !== 0) ||
        (ObjectExtensions.isNumber(props.salesAgreementLineId) && props.salesAgreementLineId !== 0)) {
        return (
            <script>
                {renderSalesAgreementPrice(props)}
            </script>
        );
    }

    return (<script>
        {showStrikethroughPricing(props) ? renderCurrentPriceWithOriginalPrice(props) : renderCurrentPrice(props)}
    </script>);
};

/**
 * The PriceComponent.
 */
export const PriceComponent: React.FunctionComponent<IPriceComponentProps> = msdyn365Commerce.createComponent<IPriceComponent>(
    'Price',
    { component: Price, ...priceComponentActions }
);
